import React, { useState } from 'react';
import { FunnelStage, deleteFunnelStage } from '@/actions/data-fetch-funnel';
import {
  Edit,
  Trash2,
  Filter,
  Calendar,
  Hash,
  Palette,
  TrendingUp,
  Settings,
  XCircle,
  Trophy
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { toastMessage } from '@/utils/toast-message';
import SettingsSlideModal from '../../../components/Modal/SettingsSlideModal';
import StatusMappingManager from './StatusMappingManager';

interface FunnelStagesTableProps {
  data: FunnelStage[];
  isLoading: boolean;
  onEdit: (stage: FunnelStage) => void;
  onDelete: () => void;
}

const FunnelStagesTable: React.FC<FunnelStagesTableProps> = ({
  data,
  isLoading,
  onEdit,
  onDelete
}) => {
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [selectedStage, setSelectedStage] = useState<FunnelStage | null>(null);
  const [isMappingModalOpen, setIsMappingModalOpen] = useState(false);

  const formatDate = (dateString: string) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch {
      return 'Unknown';
    }
  };

  const handleDelete = async (stageId: number, stageName: string) => {
    if (!confirm(`Are you sure you want to delete "${stageName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      setDeletingId(stageId);
      const response = await deleteFunnelStage(stageId);

      if (response.success) {
        toastMessage('Funnel stage deleted successfully', 'success');
        onDelete();
      } else {
        toastMessage(response.message || 'Failed to delete funnel stage', 'error');
      }
    } catch (error: any) {
      toastMessage(error.message || 'Failed to delete funnel stage', 'error');
    } finally {
      setDeletingId(null);
    }
  };

  const handleManageMappings = (stage: FunnelStage) => {
    setSelectedStage(stage);
    setIsMappingModalOpen(true);
  };

  const handleMappingClose = () => {
    setIsMappingModalOpen(false);
    setSelectedStage(null);
  };

  // Sort data by stage_order
  const sortedData = [...data].sort((a, b) => a.stage_order - b.stage_order);

  return (
    <>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 mt-6">
        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-900/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  <div className="flex items-center space-x-1">
                    <Hash className="w-4 h-4" />
                    <span>Order</span>
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  <div className="flex items-center space-x-1">
                    <Filter className="w-4 h-4" />
                    <span>Stage Name</span>
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  <div className="flex items-center space-x-1">
                    <Palette className="w-4 h-4" />
                    <span>Color</span>
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  <div className="flex items-center space-x-1">
                    <TrendingUp className="w-4 h-4" />
                    <span>Probability</span>
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  <div className="flex items-center space-x-1">
                    <Settings className="w-4 h-4" />
                    <span>Status Mappings</span>
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  <div className="flex items-center space-x-1">
                    <Calendar className="w-4 h-4" />
                    <span>Created</span>
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center">
                    <div className="flex items-center justify-center space-x-2">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-600"></div>
                      <span className="text-gray-500 dark:text-gray-400">Loading funnel stages...</span>
                    </div>
                  </td>
                </tr>
              ) : sortedData.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                    No funnel stages found. Create your first stage to get started.
                  </td>
                </tr>
              ) : (
                sortedData.map((stage) => (
                  <tr key={stage.stage_id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-brand-100 dark:bg-brand-900/20 text-brand-600 dark:text-brand-400 font-semibold text-sm">
                        {stage.stage_order}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-3">
                        <div
                          className="flex-shrink-0 h-10 w-10 rounded-lg flex items-center justify-center"
                          style={{ backgroundColor: stage.color_code ? `${stage.color_code}20` : '#3b82f620' }}
                        >
                          <Filter className="h-5 w-5" style={{ color: stage.color_code || '#3b82f6' }} />
                        </div>
                        <div>
                          <div className="flex items-center space-x-2">
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                              {stage.stage_name}
                            </div>
                            {stage.is_default && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400">
                                Default
                              </span>
                            )}
                            {stage.is_win && (
                              <Trophy className="w-4 h-4 text-green-500" aria-label="Win stage" />
                            )}
                            {stage.is_lost && (
                              <XCircle className="w-4 h-4 text-red-500" aria-label="Lost stage" />
                            )}
                          </div>
                          {stage.stage_description && (
                            <div className="text-sm text-gray-500 dark:text-gray-400 max-w-xs truncate">
                              {stage.stage_description}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <div
                          className="w-6 h-6 rounded-full border-2 border-gray-300 dark:border-gray-600"
                          style={{ backgroundColor: stage.color_code || '#6b7280' }}
                        ></div>
                        <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                          {stage.color_code || 'N/A'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {stage.probability_percentage !== null && stage.probability_percentage !== undefined ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-brand-100 text-brand-800 dark:bg-brand-900/20 dark:text-brand-400">
                          {stage.probability_percentage}%
                        </span>
                      ) : (
                        <span className="text-sm text-gray-400 dark:text-gray-500">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleManageMappings(stage)}
                        className="inline-flex items-center space-x-1 px-3 py-1.5 rounded-lg text-sm font-medium bg-brand-50 text-brand-700 hover:bg-brand-100 dark:bg-brand-900/20 dark:text-brand-400 dark:hover:bg-brand-900/30 transition-colors"
                      >
                        <Settings className="w-4 h-4" />
                        <span>{stage.statusMappings?.length || 0} mapped</span>
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {formatDate(stage.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                      <button
                        onClick={() => onEdit(stage)}
                        className="inline-flex items-center px-3 py-1.5 bg-brand-50 text-brand-700 rounded-lg hover:bg-brand-100 dark:bg-brand-900/20 dark:text-brand-400 dark:hover:bg-brand-900/30 transition-colors"
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(stage.stage_id, stage.stage_name)}
                        disabled={deletingId === stage.stage_id}
                        className="inline-flex items-center px-3 py-1.5 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/30 transition-colors disabled:opacity-50"
                      >
                        {deletingId === stage.stage_id ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600 mr-1"></div>
                        ) : (
                          <Trash2 className="w-4 h-4 mr-1" />
                        )}
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Status Mapping Modal */}
      <SettingsSlideModal
        isOpen={isMappingModalOpen}
        onClose={handleMappingClose}
        title={`Manage Status Mappings - ${selectedStage?.stage_name}`}
      >
        {selectedStage && (
          <StatusMappingManager
            stage={selectedStage}
            onClose={handleMappingClose}
          />
        )}
      </SettingsSlideModal>
    </>
  );
};

export default FunnelStagesTable;
